import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { attachReceivableOrigins, type ReceivableOrigin, type StayPlanChargeLink } from "@/lib/daycare-hotel/receivableOrigins";
import { STAY_PLAN_TABLES } from "@/lib/daycare-hotel/stayPlans";
import { splitAmountByPet } from "@/lib/sales/pet-split";

type SaleStatus = Database["public"]["Enums"]["sale_status"];
type PaymentMethod = Database["public"]["Enums"]["payment_method"];
type AppointmentStatus = Database["public"]["Tables"]["appointments"]["Row"]["status"];

type RawSalePayment = {
  id: string;
  amount: number | null;
  method: PaymentMethod | null;
  received_at: string | null;
  reference: string | null;
  created_at: string | null;
};

type RawSaleItem = {
  id: string;
  item_name: string;
  pet_id?: string | null;
  line_total?: number | null;
};

type RawSale = {
  id: string;
  sale_number: string;
  created_at: string | null;
  status: SaleStatus | null;
  total: number | null;
  subtotal: number | null;
  discount_amount: number | null;
  notes: string | null;
  appointment_id: string | null;
  pet_id: string | null;
  pet?: { name?: string | null } | null;
  sale_items: RawSaleItem[] | null;
  sale_payments: RawSalePayment[] | null;
};

type RawAppointment = {
  id: string;
  starts_at: string | null;
  status: AppointmentStatus | null;
  type: string | null;
  deleted_at?: string | null;
  pet_id?: string | null;
  pet?: { name?: string | null } | null;
  clinic_appointment_type?: { id: string; name: string; color: string; icon: string; behavior: string } | null;
  appointment_items?: Array<{
    id: string;
    qty: number | null;
    title_snapshot: string | null;
    item?: { id: string; name: string | null; sale_price: number | null } | null;
  }> | null;
};

export interface FinancialSale {
  id: string;
  saleNumber: string;
  createdAt: string | null;
  status: SaleStatus | null;
  total: number;
  subtotal: number;
  discountAmount: number;
  notes: string | null;
  appointmentId: string | null;
  petId: string | null;
  petName: string | null;
  description: string;
  /** Linhas da venda com o pet de cada uma (sale_items.pet_id); ausente em vendas montadas à mão. */
  items?: Array<{ id: string; itemName: string; petId: string | null; lineTotal: number }>;
  payments: Array<{
    id: string;
    amount: number;
    method: PaymentMethod;
    receivedAt: string | null;
    reference: string | null;
    createdAt: string | null;
  }>;
}

export interface FinancialReceivableRow {
  saleId: string;
  saleNumber: string;
  saleDate: string | null;
  description: string;
  amountOpen: number;
  pendingCount: number;
  petId: string | null;
  petName: string | null;
  origin: ReceivableOrigin | null;
}

export interface FinancialPaymentHistoryRow {
  paymentId: string;
  saleId: string;
  saleNumber: string;
  saleDate: string | null;
  paymentDate: string | null;
  method: PaymentMethod;
  amount: number;
  reference: string | null;
  description: string;
  petId: string | null;
  petName: string | null;
}

export interface FinancialOpenAppointmentRow {
  appointmentId: string;
  startsAt: string | null;
  status: AppointmentStatus | null;
  type: string | null;
  clinic_appointment_type?: { id: string; name: string; color: string; icon: string; behavior: string } | null;
  estimatedTotal: number;
  petId: string | null;
  petName: string | null;
}

export interface FinancialSummary {
  totalOpen: number;
  openCount: number;
  totalReceived: number;
  lastReceivedAt: string | null;
  lastChargeAt: string | null;
  openAppointmentsCount: number;
  openAppointmentsTotal: number;
}

interface FinancialSummaryResult {
  summary: FinancialSummary;
  openReceivables: FinancialReceivableRow[];
  paymentHistory: FinancialPaymentHistoryRow[];
  sales: FinancialSale[];
  openAppointments: FinancialOpenAppointmentRow[];
}

type FinancialScope =
  | { scope: "patient"; petId?: string | null }
  | { scope: "owner"; ownerId?: string | null; petIds?: string[] };

const EXCLUDED_SALE_STATUSES: SaleStatus[] = ["CANCELADA", "DEVOLVIDA"];
const CLOSED_APPOINTMENT_STATUSES: AppointmentStatus[] = ["finalizado", "pago", "cancelado", "faltou"];

const emptyResult: FinancialSummaryResult = {
  summary: {
    totalOpen: 0,
    openCount: 0,
    totalReceived: 0,
    lastReceivedAt: null,
    lastChargeAt: null,
    openAppointmentsCount: 0,
    openAppointmentsTotal: 0,
  },
  openReceivables: [],
  paymentHistory: [],
  sales: [],
  openAppointments: [],
};

const describeItemNames = (names: string[], saleNumber: string) => {
  const itemNames = names.map((name) => name?.trim()).filter((name): name is string => Boolean(name));

  if (itemNames.length === 0) return `Venda #${saleNumber}`;
  if (itemNames.length <= 2) return itemNames.join(", ");
  return `${itemNames.slice(0, 2).join(", ")} +${itemNames.length - 2}`;
};

const buildSaleDescription = (sale: RawSale) =>
  describeItemNames((sale.sale_items ?? []).map((item) => item.item_name), sale.sale_number);

const normalizeSales = (rows: RawSale[]): FinancialSale[] =>
  rows.map((sale) => ({
    id: sale.id,
    saleNumber: sale.sale_number,
    createdAt: sale.created_at,
    status: sale.status,
    total: Number(sale.total ?? 0),
    subtotal: Number(sale.subtotal ?? 0),
    discountAmount: Number(sale.discount_amount ?? 0),
    notes: sale.notes ?? null,
    appointmentId: sale.appointment_id ?? null,
    petId: sale.pet_id ?? null,
    petName: sale.pet?.name ?? null,
    description: buildSaleDescription(sale),
    items: (sale.sale_items ?? []).map((item) => ({
      id: item.id,
      itemName: item.item_name,
      petId: item.pet_id ?? null,
      lineTotal: Number(item.line_total ?? 0),
    })),
    payments: (sale.sale_payments ?? [])
      .filter((payment): payment is RawSalePayment & { method: PaymentMethod } => Boolean(payment.id && payment.method))
      .map((payment) => ({
        id: payment.id,
        amount: Number(payment.amount ?? 0),
        method: payment.method,
        receivedAt: payment.received_at ?? null,
        reference: payment.reference ?? null,
        createdAt: payment.created_at ?? null,
      })),
  }));

const normalizeOpenAppointments = (rows: RawAppointment[]): FinancialOpenAppointmentRow[] =>
  rows
    .filter((appointment) => {
      if (appointment.deleted_at) return false;
      if (appointment.status && CLOSED_APPOINTMENT_STATUSES.includes(appointment.status)) return false;
      const startsAtTs = appointment.starts_at ? new Date(appointment.starts_at).getTime() : Number.NaN;
      if (Number.isNaN(startsAtTs)) return false;
      return startsAtTs <= Date.now();
    })
    .map((appointment) => ({
      appointmentId: appointment.id,
      startsAt: appointment.starts_at,
      status: appointment.status ?? null,
      type: appointment.type ?? null,
      clinic_appointment_type: appointment.clinic_appointment_type ?? null,
      estimatedTotal: (appointment.appointment_items ?? []).reduce(
        (sum, item) => sum + Number(item.qty ?? 1) * Number(item.item?.sale_price ?? 0),
        0,
      ),
      petId: appointment.pet_id ?? null,
      petName: appointment.pet?.name ?? null,
    }));

export type OpenReceivableRow = Omit<FinancialReceivableRow, "origin">;

/**
 * Uma venda `ABERTA` sem nenhum `sale_payments` registrado (caso da
 * mensalidade de plano, que nasce só com `sale_items`) é recebível pelo
 * valor cheio — não só pelas parcelas explicitamente marcadas `A_RECEBER`.
 */
export function computeOpenReceivables(activeSales: FinancialSale[]): OpenReceivableRow[] {
  return activeSales
    .filter((sale) => sale.status === "ABERTA")
    .map((sale) => {
      const received = sale.payments
        .filter((payment) => Boolean(payment.receivedAt))
        .reduce((sum, payment) => sum + payment.amount, 0);
      const pending = sale.payments.filter((payment) => !payment.receivedAt).length;
      return {
        saleId: sale.id,
        saleNumber: sale.saleNumber,
        saleDate: sale.createdAt,
        description: sale.description,
        amountOpen: Math.max(0, sale.total - received),
        pendingCount: pending > 0 ? pending : 1,
        petId: sale.petId,
        petName: sale.petName,
      };
    })
    .filter((sale) => sale.amountOpen > 0)
    .sort((a, b) => (b.saleDate ? new Date(b.saleDate).getTime() : 0) - (a.saleDate ? new Date(a.saleDate).getTime() : 0));
}

/**
 * Histórico de pagamentos recebidos visto de um pet. Numa venda de mais de um pet cada um fica com a parte dele
 * do pagamento (proporcional aos itens).
 * Venda cujos itens não têm pet (antigas) pertence inteira ao pet da venda.
 */
export function buildPetPaymentHistory(
  activeSales: FinancialSale[],
  petId: string,
  petNames: Map<string, string>,
): FinancialPaymentHistoryRow[] {
  const nameOf = (id: string | null) => (id ? (petNames.get(id) ?? null) : null);

  return activeSales
    .filter((sale) => !sale.status || !EXCLUDED_SALE_STATUSES.includes(sale.status))
    .flatMap((sale) => {
      const lines = (sale.items ?? []).map((item) => ({ petId: item.petId, lineTotal: item.lineTotal }));
      const petItemNames = (sale.items ?? [])
        .filter((item) => (item.petId ?? sale.petId) === petId)
        .map((item) => item.itemName);

      return sale.payments
        .filter((payment) => Boolean(payment.receivedAt))
        .flatMap((payment) => {
          const parts = splitAmountByPet(payment.amount, lines, sale.petId);
          const mine = parts.find((part) => part.petId === petId);
          if (!mine) return [];

          return [{
            paymentId: payment.id,
            saleId: sale.id,
            saleNumber: sale.saleNumber,
            saleDate: sale.createdAt,
            paymentDate: payment.receivedAt,
            method: payment.method,
            amount: mine.amount,
            reference: payment.reference,
            description: petItemNames.length > 0 ? describeItemNames(petItemNames, sale.saleNumber) : sale.description,
            petId,
            petName: nameOf(petId),
          }];
        });
    })
    .sort((a, b) => (b.paymentDate ? new Date(b.paymentDate).getTime() : 0) - (a.paymentDate ? new Date(a.paymentDate).getTime() : 0));
}

export function useFinancialSummary(params: FinancialScope) {
  const { scope } = params;
  const petIds = scope === "owner" ? Array.from(new Set(params.petIds ?? [])).filter(Boolean) : [];
  const ownerId = scope === "owner" ? params.ownerId ?? null : null;
  const petId = scope === "patient" ? params.petId ?? null : null;

  return useQuery({
    queryKey: ["financial-summary", scope, ownerId, petId, petIds],
    enabled: scope === "patient" ? Boolean(petId) : Boolean(ownerId),
    queryFn: async (): Promise<FinancialSummaryResult> => {
      if (scope === "patient" && !petId) return emptyResult;
      if (scope === "owner" && !ownerId) return emptyResult;

      const salesSelect = `
        id,
        sale_number,
        created_at,
        status,
        total,
        subtotal,
        discount_amount,
        notes,
        appointment_id,
        pet_id,
        pet:pets!sales_pet_id_fkey(name),
        sale_items(id, item_name, pet_id, line_total),
        sale_payments(id, amount, method, received_at, reference, created_at)
      `;

      let salesRows: RawSale[] = [];

      if (scope === "patient") {
        const { data, error } = await supabase
          .from("sales")
          .select(salesSelect)
          .eq("pet_id", petId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        salesRows = (data ?? []) as unknown as RawSale[];

        // venda de mais de um pet: o pet pode aparecer só nos itens, sem ser o pet principal da venda
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- sale_items.pet_id ainda não está no types.ts gerado
        const { data: itemSales, error: itemSalesError } = await (supabase as any)
          .from("sale_items")
          .select("sale_id")
          .eq("pet_id", petId);
        if (itemSalesError) throw itemSalesError;
        const known = new Set(salesRows.map((sale) => sale.id));
        const extraSaleIds = Array.from(
          new Set(((itemSales ?? []) as Array<{ sale_id: string }>).map((row) => row.sale_id)),
        ).filter((id) => !known.has(id));
        if (extraSaleIds.length > 0) {
          const { data: extraSales, error: extraSalesError } = await supabase
            .from("sales")
            .select(salesSelect)
            .in("id", extraSaleIds);
          if (extraSalesError) throw extraSalesError;
          salesRows = [...salesRows, ...((extraSales ?? []) as unknown as RawSale[])].sort(
            (a, b) => (b.created_at ? new Date(b.created_at).getTime() : 0) - (a.created_at ? new Date(a.created_at).getTime() : 0),
          );
        }
      } else {
        const results: RawSale[] = [];
        const { data: ownerSales, error: ownerSalesError } = await supabase
          .from("sales")
          .select(salesSelect)
          .eq("owner_id", ownerId)
          .order("created_at", { ascending: false });
        if (ownerSalesError) throw ownerSalesError;
        results.push(...((ownerSales ?? []) as unknown as RawSale[]));

        if (petIds.length > 0) {
          const { data: petSales, error: petSalesError } = await supabase
            .from("sales")
            .select(salesSelect)
            .in("pet_id", petIds)
            .order("created_at", { ascending: false });
          if (petSalesError) throw petSalesError;
          results.push(...((petSales ?? []) as unknown as RawSale[]));
        }

        salesRows = Array.from(new Map(results.map((sale) => [sale.id, sale])).values());
      }

      const appointmentsSelect = `
        id,
        starts_at,
        status,
        type,
        deleted_at,
        pet_id,
        clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
        pet:pets!appointments_pet_id_fkey(name),
        appointment_items(
          id,
          qty,
          title_snapshot,
          item:items(id, name, sale_price)
        )
      `;

      let appointmentRows: RawAppointment[] = [];

      if (scope === "patient") {
        const { data, error } = await supabase
          .from("appointments")
          .select(appointmentsSelect)
          .eq("pet_id", petId)
          .order("starts_at", { ascending: true });
        if (error) throw error;
        appointmentRows = (data ?? []) as RawAppointment[];
      } else if (petIds.length > 0) {
        const { data, error } = await supabase
          .from("appointments")
          .select(appointmentsSelect)
          .in("pet_id", petIds)
          .order("starts_at", { ascending: true });
        if (error) throw error;
        appointmentRows = (data ?? []) as RawAppointment[];
      }

      const sales = normalizeSales(salesRows);
      const activeSales = sales.filter((sale) => !sale.status || !EXCLUDED_SALE_STATUSES.includes(sale.status));

      const openReceivableRows = computeOpenReceivables(activeSales);
      const openSaleIds = openReceivableRows.map((row) => row.saleId);
      const chargeLinks = new Map<string, StayPlanChargeLink>();
      if (openSaleIds.length > 0) {
        await Promise.all((["daycare", "hotel"] as const).map(async (module) => {
          const tables = STAY_PLAN_TABLES[module];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data } = await (supabase as any)
            .from(tables.charges)
            .select(`sale_id, due_on, contract:${tables.contracts}(id, pet_id, terms_snapshot)`)
            .in("sale_id", openSaleIds);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (data ?? []).forEach((charge: any) => {
            if (!charge.sale_id) return;
            chargeLinks.set(charge.sale_id, {
              saleId: charge.sale_id,
              dueOn: charge.due_on,
              module,
              contractId: charge.contract?.id ?? "",
              petId: charge.contract?.pet_id ?? null,
              planName: charge.contract?.terms_snapshot?.name ?? "",
            });
          });
        }));
      }
      const openReceivables = attachReceivableOrigins(openReceivableRows, chargeLinks);

      // visão do pet: cada pet fica com a parte dele dos pagamentos das vendas de mais de um pet
      const petNames = new Map<string, string>();
      if (scope === "patient" && petId) {
        const petIdsInSales = Array.from(new Set(
          activeSales.flatMap((sale) => [sale.petId, ...(sale.items ?? []).map((item) => item.petId)]),
        )).filter((id): id is string => Boolean(id));
        if (petIdsInSales.length > 0) {
          const { data: petRows, error: petRowsError } = await supabase
            .from("pets")
            .select("id, name")
            .in("id", petIdsInSales);
          if (petRowsError) throw petRowsError;
          (petRows ?? []).forEach((row) => petNames.set(row.id, row.name ?? ""));
        }
      }

      const paymentHistory = scope === "patient" && petId
        ? buildPetPaymentHistory(activeSales, petId, petNames)
        : activeSales
        .flatMap((sale) =>
          sale.payments
            .filter((payment) => Boolean(payment.receivedAt))
            .map((payment) => ({
              paymentId: payment.id,
              saleId: sale.id,
              saleNumber: sale.saleNumber,
              saleDate: sale.createdAt,
              paymentDate: payment.receivedAt,
              method: payment.method,
              amount: payment.amount,
              reference: payment.reference,
              description: sale.description,
              petId: sale.petId,
              petName: sale.petName,
            })),
        )
        .sort((a, b) => (b.paymentDate ? new Date(b.paymentDate).getTime() : 0) - (a.paymentDate ? new Date(a.paymentDate).getTime() : 0));

      const openAppointments = normalizeOpenAppointments(appointmentRows);

      return {
        summary: {
          totalOpen: openReceivables.reduce((sum, row) => sum + row.amountOpen, 0),
          openCount: openReceivables.reduce((sum, row) => sum + row.pendingCount, 0),
          totalReceived: paymentHistory.reduce((sum, row) => sum + row.amount, 0),
          lastReceivedAt: paymentHistory[0]?.paymentDate ?? null,
          lastChargeAt: activeSales[0]?.createdAt ?? null,
          openAppointmentsCount: openAppointments.length,
          openAppointmentsTotal: openAppointments.reduce((sum, appointment) => sum + appointment.estimatedTotal, 0),
        },
        openReceivables,
        paymentHistory,
        sales,
        openAppointments,
      };
    },
  });
}
